import { useCallback, useState } from 'react';

import {
  CancelCircleIcon,
  CheckmarkCircle02Icon,
  RestoreBinIcon,
  SentIcon,
  StopCircleIcon,
} from '@hugeicons/core-free-icons';
import { MESSAGE_STATE_ERROR, MESSAGE_STATE_SUCCESS } from 'constants/message-app-state-contants';
import { useLocale } from 'hooks/locale';
import { Badge } from 'react-bootstrap';
import Moment from 'react-moment';
import { Link } from 'react-router-dom';

import { IconComponent } from 'components/Icon/IconComponent';
import { Table } from 'components/Table/Table';

import {
  useBulkRenewCheckRenewalsMutation,
  useBulkUpdateCheckRenewalsMutation,
  useRenewCheckRenewalMutation,
  useUpdateCheckRenewalMutation,
} from 'api/check-renewals-api';

const RenewalStatuses = {
  canceled: 'Canceled',
  'in-progress': 'In Progress',
  scheduled: 'Scheduled',
  overdue: 'Overdue',
  pending: 'Pending',
};

export const CheckRenewalsPendingTab = ({
  renewalConfigs = [],
  company,
  messagePopUp = () => {},
  setLoading = () => {},
  getRenewals = () => {},
}) => {
  const [updateCheckRenewal] = useUpdateCheckRenewalMutation();
  const [renewCheckRenewal] = useRenewCheckRenewalMutation();
  const [bulkRenewCheckRenewals] = useBulkRenewCheckRenewalsMutation();
  const [bulkUpdateCheckRenewals] = useBulkUpdateCheckRenewalsMutation();

  const [pagination, setPagination] = useState({ page: 1, total_pages: 1 });
  const [recordsPerPage, setRecordsPerPage] = useState(15);
  const [checkRenewals, setCheckRenewals] = useState([]);
  const { dateSlashFormat } = useLocale(company?.locale);

  const updateCheckRenewalCallback = useCallback(
    async (id, updatedCheckRenewal) => {
      setLoading(true);
      setCheckRenewals(curr =>
        curr.map(cr =>
          cr.id === id
            ? {
                ...updatedCheckRenewal,
                candidate_name: cr.candidate_name,
                renewal_config_name: cr.renewal_config_name,
                renewal_config_frequency: cr.renewal_config_frequency,
              }
            : cr,
        ),
      );
      setLoading(false);
    },
    [updateCheckRenewal],
  );

  const onQueryUpdate = async params => {
    setLoading(true);
    const { data: result } = await getRenewals({ params: { ...params, records: recordsPerPage } });
    setCheckRenewals(result.check_renewals);
    setPagination(result.pagination);
    setLoading(false);
  };

  const handleUpdateCheckRenewal = async (id, data) => {
    setLoading(true);
    const { data: result } = await updateCheckRenewal({ id, data });
    updateCheckRenewalCallback(id, result);
    setLoading(false);
  };

  const renew = async id => {
    setLoading(true);
    try {
      const { data: result } = await renewCheckRenewal(id);
      updateCheckRenewalCallback(id, result);
    } catch (error) {
      messagePopUp('No more credits available.', MESSAGE_STATE_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const bulkStopRenewals = async (ids, useAll) => {
    setLoading(true);
    await bulkUpdateCheckRenewals({ ids: ids, useAll, fields: { canceled: true } });
    messagePopUp('Checks canceled.', MESSAGE_STATE_SUCCESS);
    setLoading(false);
  };

  const bulkApproveRenewals = async (ids, useAll) => {
    setLoading(true);
    await bulkUpdateCheckRenewals({ ids: ids, useAll, fields: { approved: true } });
    messagePopUp('Checks approved.', MESSAGE_STATE_SUCCESS);
    setLoading(false);
  };

  const bulkRenew = async (ids, useAll) => {
    setLoading(true);
    await bulkRenewCheckRenewals({ ids, use_all: useAll });
    messagePopUp('Checks set to renew. We will update them soon.', MESSAGE_STATE_SUCCESS);
    setLoading(false);
  };

  const screeningFrequency = checkRenewal => {
    if (checkRenewal.renewal_config_renewal_mode === 'use_expiration') {
      return `${checkRenewal.renewal_config_days_before_expiration} days bef. exp.`;
    } else {
      const days = parseInt(checkRenewal.renewal_config_frequency);
      if (days >= 365) {
        const years = Math.round(days / 365);
        return `${years} year${years > 1 ? 's' : ''}`;
      } else if (days >= 30) {
        const months = Math.round(days / 30);
        return `${months} month${months > 1 ? 's' : ''}`;
      } else {
        return `${days} day${days > 1 ? 's' : ''}`;
      }
    }
  };

  return (
    <Table.Context onQueryUpdate={onQueryUpdate} defaultQuery={{ page: 1, when: '30' }}>
      <Table.FloatingFilters className="u-margin--large surface-white">
        <Table.FloatingFiltersLeft className="u-gap-medium align-items-end">
          <Table.MultiSelectFilter
            title="Renewal Type"
            filterName="renewal_config_id"
            type="status"
            options={renewalConfigs?.map(rc => ({
              name: rc.name,
              value: rc.id,
            }))}
            useDefault={false}
          />
          <Table.MultiSelectFilter
            title="Status"
            filterName="status"
            type="status"
            options={[
              { name: 'Pending', value: 'pending' },
              { name: 'Scheduled', value: 'scheduled' },
              { name: 'Overdue', value: 'overdue' },
              { name: 'Canceled', value: 'canceled' },
            ]}
            useDefault={false}
          />
          <Table.MultiSelectFilter
            title="Next Renew Date"
            filterName="next_renew_date"
            type="status"
            options={[
              { name: 'Next Week', value: 'next_week' },
              { name: 'Next Month', value: 'next_month' },
              { name: 'Next 3 Months', value: 'next_3_months' },
              { name: 'Overdue', value: 'overdue' },
            ]}
          />
        </Table.FloatingFiltersLeft>
      </Table.FloatingFilters>
      <Table.TableContent className="u-margin--large">
        <Table.Filters>
          <Table.Search placeholder="Search..." />
        </Table.Filters>
        <Table className="u-margin--large">
          <Table.Head>
            <Table.Row>
              <Table.Header bulkSelector>Status</Table.Header>
              <Table.Header>Employee</Table.Header>
              <Table.Header>Renewal Type</Table.Header>
              <Table.Header>Renewal Frequency</Table.Header>
              <Table.Header>Last Renewal</Table.Header>
              <Table.Header>Next Renewal</Table.Header>
              <Table.Header>Actions</Table.Header>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {checkRenewals.map(checkRenewal => (
              <Table.Row key={checkRenewal.id}>
                <Table.Col bulkSelector selectorId={checkRenewal.id}>
                  <Badge className={`u-status-${checkRenewal.status}`}>
                    {RenewalStatuses[checkRenewal.status]}
                  </Badge>
                </Table.Col>
                <Table.Col styles={{ 'vertical-align': 'center' }}>
                  <Link to={`/employee/${checkRenewal.employee_id}`} className="u-link dark">
                    {checkRenewal.candidate_name}
                  </Link>
                </Table.Col>
                <Table.Col styles={{ 'vertical-align': 'center' }}>
                  {checkRenewal.renewal_config_name}
                </Table.Col>
                <Table.Col styles={{ 'vertical-align': 'center' }}>
                  {screeningFrequency(checkRenewal)}
                </Table.Col>
                <Table.Col styles={{ 'vertical-align': 'center' }}>
                  {checkRenewal.last_renew_at ? (
                    <Moment format={dateSlashFormat}>{checkRenewal.last_renew_at}</Moment>
                  ) : (
                    '-'
                  )}
                </Table.Col>
                <Table.Col styles={{ 'vertical-align': 'center' }}>
                  {checkRenewal.status !== 'canceled' && checkRenewal.next_renew_at ? (
                    <Moment format={dateSlashFormat}>{checkRenewal.next_renew_at}</Moment>
                  ) : (
                    '-'
                  )}
                </Table.Col>
                <Table.Col>
                  {checkRenewal.status !== 'canceled' && checkRenewal.next_renew_at && (
                    <>
                      {checkRenewal.approved ? (
                        <IconComponent
                          type="neutral-light"
                          icon={StopCircleIcon}
                          noBackground
                          tip="Stop renewal"
                          onClick={() =>
                            handleUpdateCheckRenewal(checkRenewal.id, { approved: false })
                          }
                        />
                      ) : (
                        <IconComponent
                          type="neutral-light"
                          icon={CheckmarkCircle02Icon}
                          noBackground
                          tip="Approve renewal"
                          onClick={() =>
                            handleUpdateCheckRenewal(checkRenewal.id, { approved: true })
                          }
                        />
                      )}
                      <IconComponent
                        type="neutral-light"
                        icon={SentIcon}
                        tip="Send renewal now"
                        noBackground
                        onClick={() => renew(checkRenewal.id)}
                      />
                      <IconComponent
                        type="neutral-light"
                        icon={CancelCircleIcon}
                        tip="Cancel renewal"
                        noBackground
                        onClick={() =>
                          handleUpdateCheckRenewal(checkRenewal.id, { canceled: true })
                        }
                      />
                    </>
                  )}
                  {checkRenewal.status === 'canceled' && (
                    <IconComponent
                      type="neutral-light"
                      icon={RestoreBinIcon}
                      noBackground
                      tip="Restore renewal"
                      onClick={() => handleUpdateCheckRenewal(checkRenewal.id, { canceled: false })}
                    />
                  )}
                </Table.Col>
              </Table.Row>
            ))}
            {checkRenewals.length < 1 && <Table.Placeholder>No renewals found</Table.Placeholder>}
          </Table.Body>
          <Table.Pagination
            {...pagination}
            records_per_page={recordsPerPage}
            onPageSizeChange={setRecordsPerPage}
          />
        </Table>
      </Table.TableContent>
      <Table.BulkActions>
        <Table.BulkAction
          icon={CheckmarkCircle02Icon}
          tip="Confirm renewals"
          onClick={bulkApproveRenewals}
        />
        <Table.BulkAction icon={SentIcon} tip="Send renewals now" onClick={bulkRenew} />
        <Table.BulkAction
          icon={CancelCircleIcon}
          tip="Cancel renewals"
          onClick={bulkStopRenewals}
        />
      </Table.BulkActions>
    </Table.Context>
  );
};
